import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import {
  DASHBOARD_TUTORIAL_EVENT,
  DASHBOARD_TUTORIALS,
  emitDashboardTutorialEvent,
  loadDashboardTutorialState,
  pauseDashboardTutorial,
  resumeDashboardTutorial,
  startDashboardTutorial,
  tutorialByKey,
} from "../../utils/dashboardTutorial";
import { responsiveBottomDrawerPaperSx } from "../../theme/webProfitResponsive";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
};

// WebProfit Guided Onboarding · launcher for the modular engine.
// STATUS: BLOCK_6_ENGINE_READY · replay/resume without touching business data.
// SCOPE: choose a mission; the spotlight/progression lives in DashboardTutorialEngine.
export default function DashboardTutorialControl({ project, compact = false }) {
  const theme = useTheme();
  const touchLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedKey, setSelectedKey] = React.useState("welcome");
  const [activeSession, setActiveSession] = React.useState(() => loadDashboardTutorialState(project));
  const open = Boolean(anchorEl);
  const selectedTutorial = tutorialByKey(selectedKey);

  React.useEffect(() => {
    const stored = loadDashboardTutorialState(project);
    setActiveSession(stored);
    setSelectedKey(stored?.tutorialKey || "welcome");
  }, [project?.slug, project?.id, project?._id]);

  React.useEffect(() => {
    const handleTutorialEvent = (event) => {
      const nextSession = event?.detail?.session;
      if (!nextSession) return;

      const projectKey = String(project?.slug || project?.id || project?._id || "");
      if (nextSession.projectKey && projectKey && String(nextSession.projectKey) !== projectKey) return;
      setActiveSession(nextSession);
    };

    window.addEventListener(DASHBOARD_TUTORIAL_EVENT, handleTutorialEvent);
    return () => window.removeEventListener(DASHBOARD_TUTORIAL_EVENT, handleTutorialEvent);
  }, [project?.slug, project?.id, project?._id]);

  if (!project) return null;

  const closeSelector = () => setAnchorEl(null);

  const openSelector = (anchor) => {
    const session = loadDashboardTutorialState(project);
    emitDashboardTutorialEvent("launcher_open", session || {
      version: 3,
      projectKey: String(project?.slug || project?.id || project?._id || ""),
      tutorialKey: selectedKey || "welcome",
      active: false,
      source: "manual_panel_launcher",
      step: 0,
      stageKey: "",
      startedAt: new Date().toISOString(),
    });
    setAnchorEl(anchor);
  };

  const handleStart = () => {
    // Un tutorial seleccionado manualmente siempre puede reiniciarse desde cero.
    const session = startDashboardTutorial(project, selectedKey, "manual_panel_launcher");
    setActiveSession(session);
    closeSelector();
  };

  const handleResume = () => {
    const session = resumeDashboardTutorial(project, "manual_panel_launcher");
    setActiveSession(session);
    closeSelector();
  };

  const handlePause = () => {
    const session = pauseDashboardTutorial(project, "manual_panel_launcher_pause");
    setActiveSession(session);
    closeSelector();
  };

  const selectorContent = (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <SchoolRoundedIcon sx={{ color: BRAND.orange, fontSize: 21 }} />
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.15 }}>
              Ayuda y tutoriales
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.45, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontSize: ".78rem", lineHeight: 1.4 }}>
            Elegí qué querés aprender. Podés reiniciar una guía cuando quieras, aunque ya uses WebProfit hace tiempo.
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Cerrar tutoriales" onClick={closeSelector} sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider />

      <Stack spacing={0.7} role="radiogroup" aria-label="Elegir tutorial de WebProfit">
        {DASHBOARD_TUTORIALS.map((tutorial) => {
          const selected = tutorial.key === selectedKey;
          return (
            <Button
              key={tutorial.key}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setSelectedKey(tutorial.key)}
              variant="text"
              sx={{
                display: "block",
                width: "100%",
                minHeight: 62,
                px: 1.15,
                py: 0.85,
                borderRadius: 2,
                textAlign: "left",
                textTransform: "none",
                color: BRAND.dark,
                border: selected ? "1px solid rgba(243,90,58,.38)" : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
                bgcolor: selected ? "rgba(243,90,58,.075)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
                "&:hover": {
                  bgcolor: selected ? "rgba(243,90,58,.11)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.05)",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ pt: 0.12, color: selected ? BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.42)" }}>
                  {selected ? <CheckCircleRoundedIcon sx={{ fontSize: 19 }} /> : <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: ".82rem", fontWeight: 950, lineHeight: 1.25 }}>
                    {tutorial.label}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: ".72rem", lineHeight: 1.35, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
                    {tutorial.description}
                  </Typography>
                </Box>
              </Stack>
            </Button>
          );
        })}
      </Stack>

      <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontSize: ".72rem", lineHeight: 1.4 }}>
        Las guías no se inician solas. Si pausás, podés seguir cuando quieras sin perder el avance.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
        {activeSession?.active ? (
          <Button type="button" variant="outlined" startIcon={<PauseRoundedIcon />} onClick={handlePause}
            sx={{ flex: 1, minHeight: 44, borderRadius: 2, textTransform: "none", fontWeight: 950 }}>
            Pausar tutorial
          </Button>
        ) : null}
        {activeSession?.paused && activeSession?.tutorialKey === selectedKey ? (
          <Button
            type="button"
            variant="contained"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={handleResume}
            sx={{ flex: 1, minHeight: 44, borderRadius: 2, textTransform: "none", fontWeight: 950, bgcolor: BRAND.orange, boxShadow: "none", "&:hover": { bgcolor: "#DF4D31", boxShadow: "none" } }}
          >
            Continuar tutorial
          </Button>
        ) : null}
        <Button
          type="button"
          variant={activeSession?.paused && activeSession?.tutorialKey === selectedKey ? "outlined" : "contained"}
          startIcon={activeSession?.active ? <RestartAltRoundedIcon /> : <PlayArrowRoundedIcon />}
          onClick={handleStart}
          sx={{
            flex: 1,
            minHeight: 44,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 950,
            ...(activeSession?.paused && activeSession?.tutorialKey === selectedKey ? {} : { bgcolor: BRAND.orange, boxShadow: "none", "&:hover": { bgcolor: "#DF4D31", boxShadow: "none" } }),
          }}
        >
          {activeSession?.active ? "Reiniciar" : activeSession?.paused && activeSession?.tutorialKey === selectedKey ? "Empezar de cero" : `Iniciar · ${selectedTutorial.shortLabel}`}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Tooltip title="Ayuda y tutoriales" arrow enterDelay={250}>
        <IconButton
          type="button"
          aria-label="Abrir ayuda y tutoriales"
          aria-haspopup="dialog"
          aria-expanded={open ? "true" : undefined}
          onClick={(event) => openSelector(event.currentTarget)}
          sx={{
            width: compact ? 38 : 40,
            height: compact ? 38 : 40,
            color: activeSession?.active ? BRAND.orange : BRAND.dark,
            border: activeSession?.active ? "1px solid rgba(243,90,58,.34)" : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
            bgcolor: activeSession?.active ? "rgba(243,90,58,.10)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
            boxShadow: activeSession?.active ? "0 0 0 3px rgba(243,90,58,.055)" : "none",
            "&:hover": { bgcolor: activeSession?.active ? "rgba(243,90,58,.15)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" },
          }}
        >
          <SchoolRoundedIcon sx={{ fontSize: compact ? 20 : 21 }} />
        </IconButton>
      </Tooltip>

      {touchLayout ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={closeSelector}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            role: "dialog",
            "aria-label": "Ayuda y tutoriales de WebProfit",
            sx: {
              ...responsiveBottomDrawerPaperSx,
              maxHeight: "min(84dvh, 660px)",
              p: 1.5,
              pb: "max(16px, env(safe-area-inset-bottom, 0px))",
              overflowY: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            },
          }}
        >
          {selectorContent}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={closeSelector}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              role: "dialog",
              "aria-label": "Ayuda y tutoriales de WebProfit",
              sx: {
                mt: 0.7,
                width: 390,
                maxWidth: "calc(100vw - 24px)",
                maxHeight: "min(78vh, 650px)",
                p: 1.55,
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                borderRadius: 3,
                border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
                boxShadow: "0 18px 52px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.18)",
              },
            },
          }}
        >
          {selectorContent}
        </Popover>
      )}

    </>
  );
}
